<!--
  TableColumnPicker.svelte — dropdown that chooses which optional table columns are shown.

  A table's mandatory columns are not its business: it is handed only the
  columns that may be hidden, and owns nothing but the visible set. The
  caller persists that set (a `storable`, typically) so the choice survives
  a reload, and reacts to changes through `onchange` — the picker never
  fetches anything itself, because a column may or may not cost a request.

  Usage:
    <TableColumnPicker {columns} bind:visible={$userColumns} onchange={loadUsers} />
-->
<script lang="ts">
	import { Button, Dropdown, Checkbox } from 'flowbite-svelte';
	import type { ColumnOption } from '../AppTypes';
	import { toggleColumn, isDefaultColumns } from './columnVisibility';
	import { t } from '../i18n';

	let {
		columns,
		visible = $bindable(),
		defaults = [],
		onchange,
		class: className = ''
	}: {
		/** The columns the reader may turn on, in the order they appear in the table. */
		columns: ColumnOption[];
		/** Keys of the currently shown optional columns. */
		visible: string[];
		/** The arrangement `Reset` returns to. Defaults to showing nothing. */
		defaults?: string[];
		/** Called after any change, with the new visible set. */
		onchange?: (_visible: string[]) => void;
		/** Extra classes for the wrapper. */
		class?: string;
	} = $props();

	/** Whether the table is in its default shape, in which case there is nothing to reset. */
	let atDefaults = $derived(isDefaultColumns(visible, defaults));

	let isOpen = $state(false);

	/** Show or hide one column, keeping the set in the table's own column order. */
	function toggle(key: string): void {
		const next = toggleColumn(columns, visible, key);
		visible = next;
		onchange?.(next);
	}

	/** Return the table to its default arrangement. */
	function reset(): void {
		visible = [...defaults];
		onchange?.(visible);
	}
</script>

<div class="relative {className}">
	<Button color="alternative" class="justify-between">
		<span class="flex items-center gap-2">
			<i class="fa-solid fa-table-columns h-3 w-3 shrink-0"></i>
			{$t('Columns')}
			<!-- "n of m" rather than a bare count: with most columns shown by
			     default, a lone number says nothing about what is hidden. -->
			<span
				class="bg-primary-100 text-primary-700 dark:bg-primary-600/20 dark:text-primary-300 rounded-full px-1.5 text-xs font-semibold"
			>
				{visible.length}/{columns.length}
			</span>
		</span>
		<i class="fa-solid fa-chevron-down ms-2 h-3 w-3 shrink-0"></i>
	</Button>
	<Dropdown bind:isOpen triggerDelay={0} transitionParams={{ duration: 0 }} class="w-56 p-2">
		<!-- Checkboxes rather than DropdownItems: the menu has to stay open while
		     several columns are picked, and a DropdownItem closes it on click.

		     Row styling goes to `classes.div`, never `class` — flowbite puts
		     `class` on the <input> itself, so a width there stretches the box
		     into a bar and shoves the label out of the menu. -->
		<div class="space-y-1">
			{#each columns as column (column.key)}
				<Checkbox
					classes={{ div: 'w-full cursor-pointer rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600' }}
					checked={visible.includes(column.key)}
					onchange={() => toggle(column.key)}
				>
					<span class="ms-2 flex items-center gap-2 text-sm whitespace-nowrap">
						{#if column.icon}<i class="{column.icon} w-4 shrink-0 text-xs text-gray-400"></i>{/if}
						{column.name}
					</span>
				</Checkbox>
			{/each}
		</div>
		{#if !atDefaults}
			<div class="mt-2 border-t border-gray-200 pt-2 dark:border-gray-700">
				<Button size="xs" color="alternative" class="w-full" onclick={reset}>
					<i class="fa-solid fa-rotate-left me-1.5 h-3 w-3"></i>{$t('Reset columns')}
				</Button>
			</div>
		{/if}
	</Dropdown>
</div>
